import { useApolloClient } from '@apollo/client';
import {
  Box,
  FormControl,
  Icon,
  IconSize,
  MultiSelect,
  Popover,
  Select,
  Text,
  TextFieldAppearance,
  TextFieldSize,
  theme,
  useModalState,
} from '@bechtel/gds';
import {
  ChangeControl,
  DataType,
  ListType,
  MetadataColumn,
  OperatorType,
  RequiredData,
  UiTextEnum,
} from '@bechtel/sr-types';
import { useField, useFormikContext } from 'formik';
import { find, get, isNil, isNull, isUndefined } from 'lodash';
import { FC, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import {
  EMULATED_USER_ID,
  MultiLingualText,
  SelectGridModal,
  useAppUser,
  useLanguage,
  USER_PROJECT_ID,
  useTableMetadataContext,
} from '~/components';
import { GET_SELECT_LIST_DATA } from '~/gql';
import { useDebouncedFn } from '~/hooks';
import { FormFieldContext, useFormFieldContext } from './context';
import {
  BoxStyled,
  CharTextContainerStyled,
  DateFieldStyled,
  ErrorFieldStyled,
  ErrorIconStyled,
  InputWrapperStyled,
  LabelWrapper,
  MainCommodityStyled,
  SelectBoxStyled,
  TextFieldStyled,
} from './styled';
import { FormFieldProps, InputAppearance } from './types';
const formatNumber = (val, column) => {
  const [integerPart, decimalPart] = val.split('.');
  // If there are more than allowed decimal places, slice it
  if (decimalPart && decimalPart.length > column?.decimals) {
    return `${integerPart}.${decimalPart.slice(0, column?.decimals)}`;
  } else {
    return val;
  }
};
export const TextFieldComponent: FC = () => {
  const {
    metadataColumn,
    formikFieldProps,
    formFieldProps,
    valueChanged,
    handleFocus: parentFocus,
    isChangeControl,
    convertToUpperCase,
    onBlur: onParentBlur,
  } = useFormFieldContext();
  const column = metadataColumn?.displayColumn ? metadataColumn.displayColumn : metadataColumn;

  const { setFieldValue, setFieldTouched, initialValues } = useFormikContext();
  const { autofocus, readOnly, disabled } = formFieldProps;
  //@ts-ignore formikFieldProps made as optional only for context initialization
  const [field, meta] = formikFieldProps;
  const type = column?.dataType === DataType.NUMBER ? 'number' : 'string';
  const isNumeric = column?.dataType === DataType.NUMBER;

  const getFieldAlteredValue = (value: string) => {
    if (isNumeric) {
      if (column?.decimals) {
        return parseFloat(value).toFixed(column?.decimals);
      } else {
        return value;
      }
    } else {
      if (convertToUpperCase) {
        return value.toUpperCase();
      } else {
        return value;
      }
    }
  };

  const handleChange = (event) => {
    const resultVal = isNumeric ? formatNumber(event.target.value, column) : getFieldAlteredValue(event.target.value);
    setFieldValue(
      field.name,
      resultVal && resultVal.trim() !== '' ? resultVal.slice(0, column?.columnWidth) : resultVal
    );
  };
  const handleBlur = (event) => {
    if (onParentBlur) {
      onParentBlur(event);
    }
    if (parentFocus) {
      parentFocus(false);
    }
    if (event.target.value) {
      setFieldValue(field.name, event.target.value);
    } else if (column?.defaultValue && isNumeric) {
      setFieldValue(field.name, parseFloat(column?.defaultValue).toFixed(column?.decimals));
    } else {
      setFieldValue(field.name, '');
    }
    setFieldTouched(field.name, true);
  };
  const handleFocus = () => {
    if (parentFocus) {
      parentFocus(true);
    }
  };
  const handleKeyDown = (event) => {
    if (event.key === 'Escape' && initialValues) {
      setFieldValue(field.name, initialValues[field.name]);
    }
  };

  return (
    <TextFieldStyled
      {...field}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      appearance={isChangeControl ? InputAppearance.SPECIAL : TextFieldAppearance.TRANSPARENT}
      autoFocus={autofocus}
      changed={valueChanged}
      error={meta.touched && Boolean(meta.error)}
      autoComplete="off"
      readOnly={isChangeControl ? true : readOnly}
      disabled={isChangeControl ? false : disabled}
      maxLength={column?.columnWidth}
      type={type}
      tabIndex={readOnly ? -1 : 0}
      onKeyDown={handleKeyDown}
      convertToUpperCase={convertToUpperCase}
    />
  );
};

export const DateFieldComponent = (props) => {
  const { formFieldProps, valueChanged, isChangeControl } = useFormFieldContext();
  const { readOnly, disabled } = formFieldProps;

  return (
    <Box flexGrow={{ sm: 1 }}>
      <DateFieldStyled
        dateFormat="MM/dd/yyyy"
        selected={props.selectedDate ? new Date(props.selectedDate) : null}
        onChange={props.onChange}
        showPopperArrow={props.showPopperArrow}
        size={props.size}
        icon={props.icon}
        iconStyle={props.iconStyle}
        appearance={isChangeControl ? InputAppearance.SPECIAL : InputAppearance.DEFAULT}
        readOnly={isChangeControl ? true : readOnly}
        disabled={isChangeControl ? false : disabled}
        changed={valueChanged}
        portalId="root-portal"
        {...props}
      />
    </Box>
  );
};

export const MultiSelectComponent = (props) => {
  const { formFieldProps, valueChanged, formikFieldProps, isChangeControl } = useFormFieldContext();
  const { readOnly, disabled } = formFieldProps;
  const [field] = formikFieldProps;

  return (
    <Box flexGrow={{ sm: 1 }}>
      <FormControl label="" css={{ flexGrow: 1 }}>
        <MultiSelect
          {...field}
          items={props.items}
          itemComponent={props.itemComponent}
          selectedItems={props.selectedItems ? props.selectedItems : []}
          displayKeyField={props.displayKeyField}
          loading={props.loading}
          onSelectedItemsChange={props.onSelectedItemsChange}
          readOnly={readOnly}
          disabled={disabled}
          changed={valueChanged}
          size={TextFieldSize.SMALL}
          appearance={isChangeControl ? InputAppearance.SPECIAL : TextFieldAppearance.TRANSPARENT}
        ></MultiSelect>
      </FormControl>
    </Box>
  );
};

export const SelectFieldComponent = (props) => {
  const { formFieldProps, valueChanged, formikFieldProps, isChangeControl } = useFormFieldContext();
  const { readOnly, disabled } = formFieldProps;
  const [field] = formikFieldProps;
  const placeholderValue = <MultiLingualText>Please Select</MultiLingualText>;

  return (
    <SelectBoxStyled readOnly={readOnly} disabled={disabled} changed={valueChanged}>
      <Select
        {...field}
        items={props.items}
        itemComponent={props.selectedComponent}
        selectedComponent={props.selectedComponent}
        selectedItem={props.selectedItem}
        displayKeyField={props.displayKeyField}
        loading={props.loading}
        onSelectedItemChange={props.onSelectedItemChange}
        readOnly={readOnly}
        disabled={disabled}
        changed={valueChanged}
        size={TextFieldSize.SMALL}
        dropDownIconName="angle-up"
        dropDownIconStyle="solid"
        placeholder={placeholderValue?.props?.children}
        appearance={isChangeControl ? InputAppearance.SPECIAL : TextFieldAppearance.TRANSPARENT}
        popperStyle={{
          fontSize: theme.fontSizes[300],
        }}
      ></Select>
    </SelectBoxStyled>
  );
};

interface FormFieldTextProps {
  metadataColumn?: MetadataColumn;
  isMainCommodity?: boolean;
  required?: boolean;
}

export const FormFieldText: FC<FormFieldTextProps> = ({ isMainCommodity, metadataColumn, required }) => {
  const { isAlternateLanguageUsed } = useLanguage();

  const displayRequiredAsterisk = () => {
    switch (required) {
      // if required is passed as true, no need to check metadata columns requiredData field
      case true:
        return '*';
      // if required is passed as false, dont show the asterisk and no need to check metadata columns requiredData field
      case false:
        return '';
      default:
        // if required is not passed to the form-field, check the metadataColumn for requiredData
        return (metadataColumn?.requiredData && metadataColumn?.requiredData >= RequiredData.YES) ||
          (metadataColumn?.pickListType && metadataColumn?.pickListType > RequiredData.YES)
          ? '*'
          : '';
    }
  };

  return (
    <Text color={isMainCommodity ? 'purple.500' : 'grey.900'} size="300" style={{ position: 'relative' }}>
      {isAlternateLanguageUsed ? metadataColumn?.alternateColumnPrompt : metadataColumn?.columnPrompt}
      <Text color="red.500">{displayRequiredAsterisk()}</Text>
    </Text>
  );
};

const ErrorMessagePopOver = ({ meta, metadataColumn }) => {
  const theme = useTheme();
  const [showErrorPopOver, setShowErrorPopOver] = useState<boolean>(false);
  useEffect(() => {
    setShowErrorPopOver(meta.touched && Boolean(meta.error));
  }, [meta.touched, meta.error]);

  const errorMessageRef = useRef(null);

  return (
    <>
      <ErrorFieldStyled error={metadataColumn?.columnPrompt == null}>
        <div ref={errorMessageRef}>
          {meta.touched && Boolean(meta.error) && (
            <Icon
              style={{ cursor: 'pointer' }}
              onClick={() => setShowErrorPopOver((prev) => !prev)}
              icon="circle-exclamation"
              iconStyle="solid"
              fillColor="red.700"
              size={IconSize.XSMALL}
            />
          )}
        </div>
      </ErrorFieldStyled>
      <Popover
        triggerComponentRef={errorMessageRef}
        showPopper={showErrorPopOver}
        customStyle={{
          backgroundColor: `${theme.colors.red[200]}`,
          zIndex: '1',
          color: 'red.700',
          marginTop: `-${theme.spacings[200]}`,
          boxShadow: `0 ${theme.spacings[100]} ${theme.spacings[200]} 0 rgba(132, 3, 0, 0.16)`,
        }}
        placement="bottom-start"
      >
        <Box
          py={{ sm: '100' }}
          px={{ sm: '400' }}
          justifyContent={{ sm: 'space-between' }}
          alignItems={{ sm: 'center' }}
        >
          <MultiLingualText size="300" color="red.700">
            {meta.error}
            {/* {getMultiLingualTextByKey(UiTextEnum.IS_NOT_VALID, [metadataColumn?.description])} */}
          </MultiLingualText>
          <ErrorIconStyled
            onClick={() => setShowErrorPopOver(false)}
            icon="xmark"
            iconStyle="regular"
            size={IconSize.SMALL}
            fillColor="red.700"
          />
        </Box>
      </Popover>
    </>
  );
};

const MainCommodityPopOver: FC<{
  memberName: string;
  commodityName: string;
  hasFieldPicklist: boolean;
  displayMemberId: string | undefined;
}> = ({ memberName, commodityName, hasFieldPicklist, displayMemberId }) => {
  const mainCommodityRef = useRef(null);
  const [showMainCommodityPopOver, setshowMainCommodityPopOver] = useState<boolean>(false);
  const { values } = useFormikContext();
  const loggedInProjectId = window.sessionStorage.getItem(USER_PROJECT_ID);
  const emulatedUserId = window.sessionStorage.getItem(EMULATED_USER_ID);

  const handleCommodityClick = () => {
    if (!values && (!get(values, memberName) || !hasFieldPicklist)) return;
    const newWindow = window.open(
      `../../${commodityName}/${
        hasFieldPicklist && !isUndefined(displayMemberId) ? get(values, displayMemberId) : get(values, memberName)
      }`,
      '_blank'
    );
    if (newWindow) {
      newWindow.sessionStorage.setItem(USER_PROJECT_ID, loggedInProjectId || '');
      newWindow.sessionStorage.setItem(EMULATED_USER_ID, emulatedUserId || '');
    }
  };

  return (
    <MainCommodityStyled>
      <BoxStyled
        isMainCommodity={true}
        onMouseOver={() => setshowMainCommodityPopOver(true)}
        onMouseLeave={() => setshowMainCommodityPopOver(false)}
        onClick={handleCommodityClick}
      >
        <div ref={mainCommodityRef}>
          <Icon icon="external-link" size={IconSize.XSMALL} iconStyle="regular" fillColor="white" />
        </div>
        <Popover
          triggerComponentRef={mainCommodityRef}
          showPopper={showMainCommodityPopOver}
          customStyle={{ backgroundColor: 'black', color: 'white', zIndex: 1 }}
          placement="bottom"
        >
          <Box py={{ sm: '300' }} px={{ sm: '400' }} style={{ width: 'max-content' }}>
            <MultiLingualText size="300" color="white" family="openSansCondensed">
              Show in New Tab
            </MultiLingualText>
          </Box>
        </Popover>
      </BoxStyled>
    </MainCommodityStyled>
  );
};

export const FormField: FC<PropsWithChildren<FormFieldProps>> = (props) => {
  const client = useApolloClient();
  const {
    memberName,
    inferedFieldName,
    required,
    displayMemberName,
    readOnly,
    children,
    commodityName,
    searchFieldName,
    isMainCommodity,
    changeControlCheck,
    alwaysEditable,
    convertToUpperCase = true,
    showPicker = true,
    pickListName,
    pickField,
    hasFieldPicklist = false,
    validate = true,
    metadataMemberName,
    onParentBlur,
    tagValidationType = 'TAG_ALREADY_EXISTS',
    validateInferredField = false,
    customMetadataColumn,
  } = props;
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const { hasPermission: hasFieldPermission } = useAppUser();
  const [restrictedField] = useField('isRestricted');
  const tableMetadata = useTableMetadataContext();
  const { isAlternateLanguageUsed, getMultiLingualTextByKey } = useLanguage();
  const { setFieldValue, values, initialValues, touched } = useFormikContext();
  const fieldName = inferedFieldName || displayMemberName || memberName;
  const { visible, open, close } = useModalState(false);
  const getSelectionListDataByTag = async (value: string, pickListName: string) => {
    // Await issue in useLazyQuery. Suggested workaround from forum
    // https://github.com/apollographql/apollo-client/issues/9354
    const result = await client.query({
      query: GET_SELECT_LIST_DATA,
      variables: {
        name: pickListName,
        searchInput: {
          filterInput: {
            and: [
              {
                field: searchFieldName ? searchFieldName : 'TAG',
                operator: OperatorType.EQUAL,
                value: value,
              },
            ],
          },
        },
      },
      fetchPolicy: 'cache-first',
    });

    return get(result?.data, 'getSelectListData.items', null);
  };

  const debouncedGetSelectionListDataByTag = useDebouncedFn(getSelectionListDataByTag, 400);

  const customFieldValidator = async (value): Promise<string | void> => {
    const valueChanged = value !== initialValues[fieldName];
    const columnPrompt =
      (isAlternateLanguageUsed ? metadataColumn?.alternateColumnPrompt : metadataColumn?.columnPrompt) ?? '';

    if (!value) {
      if (((metadataColumn?.requiredData && metadataColumn?.requiredData > RequiredData.NO) || required) && validate) {
        return getMultiLingualTextByKey(UiTextEnum.VALUE_CANNOT_BE_BLANK, [columnPrompt]);
      } else if (initialValues[memberName] || values[memberName]) {
        setFieldValue(memberName, '', false);
        return;
      }
    } else if (!valueChanged && touched[fieldName]) {
      setFieldValue(memberName, initialValues[memberName], false);
      return;
    }
    if (valueChanged && (memberName === 'tag' || memberName === 'name')) {
      const selectListDataItems = await debouncedGetSelectionListDataByTag(
        value,
        pickListName ? pickListName : metadataColumn?.pickListName
      );

      if (
        selectListDataItems &&
        selectListDataItems.length > 0 &&
        metadataColumn?.columnPrompt &&
        tagValidationType === 'TAG_ALREADY_EXISTS'
      ) {
        return getMultiLingualTextByKey(UiTextEnum.TAG_ALREADY_EXISTS, [columnPrompt]);
      } else if (!selectListDataItems && columnPrompt && tagValidationType === 'NO_DATA_FOUND') {
        return getMultiLingualTextByKey(UiTextEnum.NO_DATA_FOUND, [columnPrompt]);
      } else if (!selectListDataItems && columnPrompt && tagValidationType === 'TAG_DOES_NOT_EXIST') {
        return getMultiLingualTextByKey(UiTextEnum.TAG_DOES_NOT_EXIST);
      }
    }

    if (!isNil(inferedFieldName) || !(visible || valueChanged)) return; // Required validation will handle empty value validation
    if (metadataColumn?.pickListType === ListType.CONTROLLED) {
      if (metadataColumn?.relatedMemberDisplayField && metadataColumn?.pickListName) {
        const selectListDataItems = await debouncedGetSelectionListDataByTag(
          value,
          pickListName ? pickListName : metadataColumn?.pickListName
        );
        if (isNull(selectListDataItems) && metadataColumn?.columnPrompt) {
          setFieldValue(memberName, '', false);
          return getMultiLingualTextByKey(UiTextEnum.VALUE_IS_NOT_VALID, [columnPrompt]);
        } else {
          selectListDataItems && setFieldValue(memberName, selectListDataItems[0]['column1'], false);
        }
      }
    } else if (metadataColumn?.pickListType === ListType.STATIC || metadataColumn?.pickListType === ListType.REQUIRED) {
      const filter = {
        and: [
          {
            field: 'VALUE',
            operator: OperatorType.EQUAL,
            value: value,
          },
          {
            field: 'NAME',
            operator: OperatorType.EQUAL,
            value: metadataColumn?.pickListName,
          },
        ],
      };
      // Await issue in useLazyQuery. Suggested workaround from forum
      // https://github.com/apollographql/apollo-client/issues/9354
      const result = await client.query({
        query: GET_SELECT_LIST_DATA,
        variables: {
          name: 'PICK_LIST.NAME',
          searchInput: {
            filterInput: filter,
          },
        },
        fetchPolicy: 'cache-first',
      });
      const selectListDataItems = get(result?.data, 'getSelectListData.items', null);

      if (!selectListDataItems) {
        return getMultiLingualTextByKey(UiTextEnum.VALUE_IS_NOT_VALID, [columnPrompt]);
      }
    }
  };

  const metadataColumn = customMetadataColumn
    ? customMetadataColumn
    : find(tableMetadata?.metadataColumns, (item) => item.memberName == (metadataMemberName || memberName));
  const isChangeControl =
    changeControlCheck &&
    metadataColumn &&
    metadataColumn.changeControl &&
    metadataColumn.changeControl >= ChangeControl.YES;

  // Get field permissions for the user
  // User set readonly or field permission or item restriction
  const isFieldReadOnly = alwaysEditable
    ? !alwaysEditable
    : !!readOnly ||
      !(metadataColumn && hasFieldPermission(`${tableMetadata?.name}.${metadataColumn?.name}`)) ||
      restrictedField.value === true;

  const validatePickList =
    metadataColumn?.pickListType === ListType.CONTROLLED ||
    metadataColumn?.pickListType === ListType.STATIC ||
    metadataColumn?.pickListType === ListType.REQUIRED;
  const fieldResult = useField({
    name: fieldName,
    validate: validatePickList && validate ? customFieldValidator : undefined,
  });

  const [field, meta] = fieldResult;
  const valueChanged = meta.value !== meta.initialValue;

  const [focus, setFocus] = useState<boolean>(false);
  const [hover, setHover] = useState<boolean>(false);
  //const deferredSearch = useDeferredValue(meta.value);
  const selectItem = (data) => {
    if (data) {
      if (metadataColumn?.pickListType === ListType.CONTROLLED || metadataColumn?.pickListType === ListType.NONE) {
        if (inferedFieldName) {
          setFieldValue(inferedFieldName, data['tag'], validateInferredField);
        } else if (metadataColumn?.relatedMemberDisplayField && data[metadataColumn?.relatedMemberDisplayField]) {
          setFieldValue(props.displayMemberName as string, data[metadataColumn?.relatedMemberDisplayField], true);
          if (props?.displayMemberId && hasFieldPicklist) {
            setFieldValue(props?.displayMemberId, data['id'], false);
          } else {
            setFieldValue(props.memberName, props.displayMemberId ? data[props.displayMemberId] : data['id'], false);
          }
        } else {
          setFieldValue(memberName, data[pickField ?? (metadataMemberName || memberName)] ?? '', true);
        }
      } else {
        setFieldValue(memberName, data['value']), false;
      }
    }

    close();
    if (inputContainerRef.current) {
      const inputElement = inputContainerRef.current.querySelector('input');
      if (inputElement) {
        inputElement.focus();
      }
    }
  };

  const handleFocus = (focus: boolean) => {
    setFocus(focus);
  };

  const characterCount = field.value ? field.value.length : 0;
  const column = metadataColumn?.displayColumn ? metadataColumn.displayColumn : metadataColumn;
  return (
    <>
      <Box
        flexDirection={{ sm: 'column' }}
        flexGrow={{ sm: 1 }}
        onMouseLeave={() => {
          setHover(false);
        }}
      >
        <Box alignItems={{ sm: 'center' }} css={{ position: 'relative' }} onMouseOver={() => setHover(true)}>
          <InputWrapperStyled
            appearance={isChangeControl ? InputAppearance.SPECIAL : props.appearance}
            error={meta.touched && Boolean(meta.error)}
            disabled={props.disabled}
            changed={valueChanged}
            readOnly={isFieldReadOnly}
            focus={focus}
            isMainCommodity={isMainCommodity}
            className="input-wrapper"
          >
            {metadataColumn?.columnPrompt != null && (
              <LabelWrapper
                appearance={isChangeControl ? InputAppearance.SPECIAL : props.appearance}
                size={props.size}
                error={meta.touched && Boolean(meta.error)}
                disabled={props.disabled}
                changed={valueChanged}
                readOnly={isFieldReadOnly}
                focus={focus}
                isMainCommodity={isMainCommodity}
              >
                <Box justifyContent={{ sm: 'space-between' }} alignItems={{ sm: 'center' }} css={{ width: '100%' }}>
                  <FormFieldText
                    metadataColumn={metadataColumn}
                    isMainCommodity={isMainCommodity}
                    required={required}
                  />
                  <ErrorMessagePopOver meta={meta} metadataColumn={metadataColumn} />
                </Box>
              </LabelWrapper>
            )}
            <div ref={inputContainerRef} style={{ flexGrow: '1' }}>
              <Box flexGrow={{ sm: 1 }} css={{ opacity: props.disabled ? '0.9' : '1' }}>
                <FormFieldContext
                  formFieldProps={{ ...props, readOnly: isFieldReadOnly }}
                  formikFieldProps={fieldResult}
                  metadataColumn={metadataColumn}
                  valueChanged={valueChanged}
                  handleFocus={handleFocus}
                  isChangeControl={!!isChangeControl}
                  convertToUpperCase={convertToUpperCase}
                  onBlur={onParentBlur}
                >
                  {valueChanged && meta.touched && Boolean(meta.error) && metadataColumn?.columnPrompt === null && (
                    <ErrorMessagePopOver meta={meta} metadataColumn={metadataColumn} />
                  )}
                  {children}
                </FormFieldContext>
              </Box>
            </div>
            {!isChangeControl && metadataColumn?.pickListName !== null && showPicker && hover && !isFieldReadOnly && (
              <BoxStyled
                isMainCommodity={false}
                onClick={() => {
                  open();
                }}
              >
                <Icon icon="ellipsis" iconStyle="solid" fillColor="primary.700" />
              </BoxStyled>
            )}
            {props.isMainCommodity &&
              hover &&
              commodityName &&
              values &&
              (values[memberName] ||
                (hasFieldPicklist && props?.displayMemberId && get(values, props?.displayMemberId))) && (
                <MainCommodityPopOver
                  memberName={memberName}
                  commodityName={commodityName}
                  hasFieldPicklist={hasFieldPicklist}
                  displayMemberId={props?.displayMemberId}
                />
              )}
          </InputWrapperStyled>
        </Box>
        {focus && column?.dataType === DataType.STRING && column?.columnWidth && (
          <CharTextContainerStyled>
            <Text>{`(${characterCount}/${column?.columnWidth})`}</Text>
          </CharTextContainerStyled>
        )}
      </Box>
      {visible && !!metadataColumn?.pickListName && !isNil(metadataColumn?.pickListType) && (
        <SelectGridModal
          close={close}
          title={`Select ${metadataColumn?.columnPrompt}`}
          listName={pickListName ? pickListName : metadataColumn?.pickListName}
          listType={metadataColumn?.pickListType}
          selectItem={selectItem}
        />
      )}
    </>
  );
};
